/**
 * 메일 시스템 API 클라이언트.
 *
 * 설계 의도:
 * - 백엔드 API 엔드포인트를 한 곳에 모음 (URL 변경 시 영향 최소화)
 * - 각 함수는 백엔드 컨트롤러 1:1 매핑
 * - 응답 형식은 백엔드 PagedResponse 또는 단순 객체 반환
 *
 * 폴더 enum (백엔드 MailFolder 와 동일):
 *   INBOX | SENT | DRAFT | SPAM | TRASH | ARCHIVE
 */
import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const BASE = `${API_BASE}/mail`;

export const mailApi = {
    // ── 목록 조회 ─────────────────────────────────────
    /** 폴더별 메일 목록 (페이지네이션) */
    listByFolder: (folder, page = 0, size = 30) =>
        api.get(BASE, { folder, page, size }),

    /** 내게 쓴 메일함 — SENT 중 본인이 수신자에 포함된 메일 */
    listSelfMails: (page = 0, size = 30) =>
        api.get(`${BASE}/self`, { page, size }),

    /** 안 읽은 메일 카운트 (사이드바 뱃지) */
    unreadCounts: () => api.get(`${BASE}/unread-counts`),

    /** 폴더별 전체 메일 카운트 (사이드바 폴더명 옆 표시) */
    totalCounts: () => api.get(`${BASE}/total-counts`),

    /** 전체 메일 — 커스텀 폴더로 옮긴 메일까지 포함(INBOX+CUSTOM+ARCHIVE) */
    listAll: (page = 0, size = 30) => api.get(`${BASE}/all`, { page, size }),

    /** 특정 사용자 정의 폴더의 메일 목록 */
    listCustomFolder: (folderId, page = 0, size = 30) =>
        api.get(`${BASE}/custom`, { folderId, page, size }),

    /** 사용자 정의 폴더별 카운트 — { total: {folderId:n}, unread: {folderId:n} } */
    customCounts: () => api.get(`${BASE}/custom-counts`),

    /**
     * 전체 메일 검색 (폴더 무관, 휴지통 제외)
     * subject / from_addr / from_name 에 LIKE 매칭. 본문 검색은 미지원.
     */
    search: (q, page = 0, size = 30) =>
        api.get(`${BASE}/search`, { q, page, size }),

    // ── 상세 조회 ─────────────────────────────────────
    /** 메일 상세 (본문 HTML/Text 포함) */
    getDetail: (mailId) => api.get(`${BASE}/${mailId}`),

    /** 같은 스레드의 모든 메일 */
    getThread: (threadId) => api.get(`${BASE}/thread/${threadId}`),

    // ── 폴더·읽음·별표 ────────────────────────────────
    /** 폴더 이동 (휴지통 포함). to=폴더명 query 파라미터 */
    move: (mailId, folder) => api.post(`${BASE}/${mailId}/move?to=${folder}`),

    /**
     * 일괄 이동 — 체크박스로 선택한 다중 메일을 한 번에 이동.
     * body: { mailIds: [1,2,3], to: "TRASH" }
     *
     * <b>응답 형식이 바뀌었다</b> (구: 이동 건수 Integer → 신: 객체).
     *   { moved: 12, failed: [{ mailId: 34, reason: "예약 발송 대기 중인 메일은 이동할 수 없습니다" }] }
     * - moved  : 실제로 이동에 성공한 건수. 선택 건수와 다를 수 있다.
     * - failed : 실패 배열. 비어 있으면 [] (null 아님). reason 은 사용자에게 그대로 보여줄 수 있는 한국어.
     * 건별 독립 트랜잭션이라 <b>부분 성공이 정상 케이스</b> — 호출부는 moved/failed 를 반드시 읽어
     * "N건 처리, M건 실패" 로 사실대로 표시해야 한다.
     * mailIds 가 비었거나 folder 가 null 이면 200 + { moved: 0, failed: [] }.
     */
    batchMove: (mailIds, folder) =>
        api.post(`${BASE}/batch/move`, { mailIds, to: folder }),

    /** 휴지통·스팸함에서 복원 (이전 폴더로, 없으면 INBOX) */
    restore: (mailId) => api.post(`${BASE}/${mailId}/restore`),

    /**
     * 일괄 복원 — 휴지통/스팸함의 메일을 <b>원래 있던 폴더</b>로 되돌린다.
     * body: { mailIds: [1,2,3] }
     * 응답: { restored: 12, failed: [{ mailId, reason }] }  (batchMove 와 같은 부분 성공 규약)
     *
     * previousFolder 가 없거나 그 커스텀 폴더가 삭제됐으면 INBOX 로 폴백한다.
     * 스팸 해제도 batchMove(ids,"INBOX") 가 아니라 이 API 를 써야 커스텀 폴더 원위치가 유지된다.
     */
    batchRestore: (mailIds) => api.post(`${BASE}/batch/restore`, { mailIds }),

    /** 영구 삭제 (본인 시야에서만) — 휴지통의 본인 메일을 완전히 제거. 원본·타 수신자는 유지, 되돌릴 수 없음. */
    deleteForever: (mailId) => api.post(`${BASE}/${mailId}/delete-forever`),

    /** 일괄 영구 삭제 (본인) — body: { mailIds: [...] }, 응답: 삭제 성공 개수 */
    batchDeleteForever: (mailIds) => api.post(`${BASE}/batch/delete-forever`, { mailIds }),

    /** 읽음 처리 — read_at 자동 채움 */
    markRead: (mailId) => api.post(`${BASE}/${mailId}/read`),

    /** 안 읽음 처리 (사용자가 다시 N으로 표시) */
    markUnread: (mailId) => api.post(`${BASE}/${mailId}/unread`),

    /** 별표 토글 */
    toggleStar: (mailId) => api.post(`${BASE}/${mailId}/star`),

    // ── 사용자 정의 폴더 ──────────────────────────────
    /** 내 폴더 목록 — [{ id, name, sortOrder, color }] */
    listFolders: () => api.get(`${BASE}/folders`),

    /** 폴더 생성 — body: { name, color? } */
    createFolder: (data) => api.post(`${BASE}/folders`, data),

    /** 폴더 수정 — body: { name?, color?, sortOrder? } */
    updateFolder: (id, data) => api.put(`${BASE}/folders/${id}`, data),

    /** 폴더 삭제 — 안의 메일은 받은 메일함(INBOX)으로 이동됨 */
    deleteFolder: (id) => api.delete(`${BASE}/folders/${id}`),

    /** 메일 1건을 사용자 폴더로 이동 */
    moveToFolder: (mailId, folderId) =>
        api.post(`${BASE}/${mailId}/move-to-folder?folderId=${folderId}`),

    /**
     * 여러 메일을 사용자 폴더로 일괄 이동 — body: { mailIds, folderId }
     *
     * <b>응답 형식이 바뀌었다</b> (구: 이동 건수 Integer → 신: batchMove 와 동일한 객체).
     *   { moved: 12, failed: [{ mailId: 34, reason: "예약 발송 대기 중인 메일은 이동할 수 없습니다" }] }
     * 커스텀 폴더 이동도 예약 발송 대기·임시보관함 메일을 거부하므로 부분 성공이 정상 케이스다.
     */
    batchMoveToFolder: (mailIds, folderId) =>
        api.post(`${BASE}/batch/move-to-folder`, { mailIds, folderId }),

    // ── 작성·발송 ────────────────────────────────────
    /**
     * 임시저장 — 제목·받는이·본문만. <b>첨부는 담지 않는다.</b>
     *
     * 왜 첨부가 빠지나
     *   초안에 첨부를 실으면 발송하지도 않을 파일이 S3 에 쌓이고, 그 초안이 지워지지 않는 한
     *   계속 남는다. 임시보관함에 몇 년 치가 누적된 전례가 있어(운영 525건·259MB) 무게를 지고 가지 않는다.
     *   그래서 작성 화면은 첨부가 있으면 저장 전에 사용자에게 확인을 받는다.
     *
     * 사용자가 '임시저장' 을 누를 때만 호출한다. 자동 임시저장은 되살리지 않는다 —
     * 10초 주기로 돌던 시절 race condition · 의도치 않은 draft 누적 · 서버 부하를 만들어 걷어낸 기능이다.
     *
     * @param data {id?, tos, ccs, bccs, subject, bodyHtml, bodyText, inReplyTo, references, parentMailId}
     *             id 가 있으면 그 초안을 갱신(남의 초안이면 서버가 403).
     * @return 저장된 메일 id
     */
    saveDraft: (data) => api.post(`${BASE}/draft`, data),

    /** 임시저장 삭제 — 서버는 완전 삭제가 아니라 휴지통으로 옮긴다(메일 영구 보존 정책). */
    deleteDraft: (mailId) => api.delete(`${BASE}/draft/${mailId}`),

    /** 발송 (DRAFT → SES SendRawEmail → SENT) */
    send: (mailId) => api.post(`${BASE}/${mailId}/send`),

    /** 새 메일 작성 + 즉시 발송 (DRAFT 단계 생략) — 첨부 없는 가벼운 흐름 */
    composeAndSend: (data) => api.post(`${BASE}/send`, data),

    /**
     * 통합 발송 — 즉시/예약/단체를 <b>한 엔드포인트</b>로 보낸다. 분기는 서버가 한다.
     *
     * 왜 통합했나
     *   예전엔 작성 창은 하나인데 호출처가 셋(send-atomic / schedule-atomic / bulk)이라
     *   "어디로 보낼지"를 프론트가 골랐다. 그 탓에 모드마다 payload 조립·검증·후처리가 갈라졌고
     *   첨부 크기 검증이 단체에만 없거나, 발송 후 연락처 등록 추천이 즉시 발송에서만 뜨는 식으로
     *   기능이 새어나갔다. 이제 sendMode 만 실어 보내고 나머지는 서버가 책임진다.
     *
     * payload: {
     *   sendMode: "IMMEDIATE" | "SCHEDULED" | "BULK",
     *   scheduledAt: "YYYY-MM-DDTHH:mm" | null,   // SCHEDULED 필수, BULK 선택
     *   tos, ccs, bccs, subject, bodyHtml, bodyText, inReplyTo, references, id?
     * }
     * files: File[] — 세 모드 모두 지원(단체는 배치당 1벌만 저장되고 수신자별 메일이 공유 참조).
     *
     * 응답: { mode, mailId, batchId, recipientCount, scheduledAt } — 모드가 달라도 한 모양.
     *
     * onProgress(0~100) — 첨부 업로드 진행률 콜백(선택).
     *   브라우저가 multipart 본문을 얼마나 보냈는지는 <b>정확히 알 수 있으므로</b> 그 구간만 실제 %로 보여준다.
     *   업로드가 끝난 뒤 서버가 하는 일(S3 저장 → EML 조립 → SES 발송)은 단일 동기 요청이라
     *   브라우저에서 관측할 수단이 없다 — 그 구간은 '발송 중' 불확정 표시로 둔다.
     *   (서버 단계까지 실시간으로 보여주려면 발송을 비동기 작업으로 바꾸고 SSE/폴링 채널이 필요한데,
     *    그러면 "실패 시 전부 롤백" 이라는 단일 트랜잭션 보장이 깨진다 — 신뢰성과 맞바꾸는 선택이라 하지 않았다)
     *
     * 실패 응답: { error:"MAIL_SEND_FAILED", message, phase, retryable, rolledBack:true }
     *   phase 로 어느 단계에서 실패했는지 알 수 있고, rolledBack=true 는 <b>메일이 나가지 않았고
     *   저장된 것도 모두 취소됐다</b>는 뜻이다.
     */
    dispatch: (payload, files = [], onProgress) => {
        const fd = new FormData();
        fd.append("payload", new Blob([JSON.stringify(payload)], { type: "application/json" }));
        for (const f of files) fd.append("files", f, f.name);
        return api.upload(`${BASE}/dispatch`, fd, onProgress ? {
            onUploadProgress: (evt) => {
                // evt.total 은 서버가 아니라 브라우저가 아는 값이라 항상 정확하다.
                // 다만 압축·프록시 환경에서 total 이 비는 경우가 있어 방어한다.
                if (!evt || !evt.total) return;
                onProgress(Math.min(100, Math.round((evt.loaded * 100) / evt.total)));
            },
        } : undefined);
    },

    /**
     * Atomic 발송 — payload + 첨부 파일을 한 multipart 요청으로 발송.
     * ※ 구 경로. 신규 코드는 dispatch({ sendMode: "IMMEDIATE", ... }) 를 쓴다.
     * - payload: JSON (tos/ccs/bccs/subject/bodyHtml/bodyText/inReplyTo/references/id?)
     * - files: File[] — 직접 메모리에서 multipart 로 첨부
     *
     * 응답 200 = 모든 게 정상 처리됨 (사이즈 검증 + S3 업로드 + SES 발송 모두).
     * 응답 4xx/5xx = 어느 단계에서든 실패 (부분 데이터 남지 않음).
     */
    composeAndSendAtomic: (payload, files = []) => {
        const fd = new FormData();
        fd.append("payload", new Blob([JSON.stringify(payload)], { type: "application/json" }));
        for (const f of files) fd.append("files", f, f.name);
        return api.upload(`${BASE}/send-atomic`, fd);
    },

    /**
     * 예약 발송 — send-atomic 과 동일 payload + scheduledAt(KST, "YYYY-MM-DDTHH:mm").
     * 즉시 발송하지 않고 예약 시각에 디스패처가 발송.
     */
    scheduleSendAtomic: (payload, scheduledAt, files = []) => {
        const fd = new FormData();
        fd.append("payload", new Blob([JSON.stringify(payload)], { type: "application/json" }));
        fd.append("scheduledAt", scheduledAt);
        for (const f of files) fd.append("files", f, f.name);
        return api.upload(`${BASE}/schedule-atomic`, fd);
    },

    /** 예약 취소 — 예약 메일을 임시보관함으로 되돌림 */
    cancelScheduled: (mailId) => api.post(`${BASE}/${mailId}/cancel-scheduled`),

    /** 예약 발송 이력 — 예약대기/발송완료/실패 상태 포함(발송 후에도 사라지지 않음). */
    listScheduledSends: (page = 0, size = 50) => api.get(`${BASE}/scheduled`, { page, size }),

    // ── 단체(개인별 개별) 발송 ─────────────────────────
    /** 단체 발송 생성 — body: { subject, bodyHtml, bodyText, recipients?, recipientsText? } */
    createBulk: (data) => api.post(`${BASE}/bulk`, data),

    /** 내 단체 발송 목록 (진행 상황) */
    listBulk: (page = 0, size = 20) => api.get(`${BASE}/bulk`, { page, size }),

    /** 단체 발송 상세 — 수신자별 진행/결과 포함 */
    getBulk: (batchId) => api.get(`${BASE}/bulk/${batchId}`),

    // ── 답장·전달 ────────────────────────────────────
    /**
     * 답장 작성 시 자동 채워질 데이터 가져오기.
     * 응답: { tos, ccs, subject, inReplyTo, references, originalQuote }
     */
    prepareReply: (mailId, replyAll = false) =>
        api.get(`${BASE}/reply/${mailId}`, { replyAll }),

    prepareForward: (mailId) => api.get(`${BASE}/forward/${mailId}`),

    // ── 받는이 자동완성 ─────────────────────────────
    /**
     * 직원 자동완성 — 이름·이메일 prefix 검색.
     * 응답: [{ id, name, email, department, position }]
     * email 은 풀 주소 ({prefix}@gayasoft.in 형태로 백엔드가 도메인 결합).
     */
    suggestEmployees: (q, size = 8) =>
        api.get(`${BASE}/employees/suggest`, { q: q || "", size }),

    /**
     * 통합 자동완성 — 직원 + 내 개인 연락처.
     * 응답: { employees: [...], contacts: [...] }
     */
    suggestRecipients: (q, size = 8) =>
        api.get(`${BASE}/recipients/suggest`, { q: q || "", size }),

    /** 기본 발송 도메인 — 백엔드 application.yml 의 mail-system.send.default-domain */
    getSendDomain: () => api.get(`${BASE}/send-domain`),

    // ── 첨부파일 ─────────────────────────────────────
    /**
     * 첨부 다운로드 — 백엔드 프록시 경유 (인증 헤더 포함, blob 반환).
     * @returns axios response 객체 — `.data` 가 Blob, `.headers["content-disposition"]` 에 파일명.
     */
    downloadAttachment: (attachmentId) =>
        api.download(`${BASE}/attachments/${attachmentId}`),

    /**
     * 첨부 미리보기 — PDF·이미지·텍스트는 원본, 오피스는 PDF 변환본을 inline 으로 blob 반환.
     * @returns axios response 객체 — `.data` 가 Blob.
     */
    previewAttachment: (attachmentId) =>
        api.download(`${BASE}/attachments/${attachmentId}/preview`),

    /*
     * uploadAttachment / deleteAttachment 래퍼는 제거됐다 (호출처 0건).
     *
     * 둘 다 "발송 전에 서버에 DRAFT 를 만들고 거기에 첨부를 붙였다 뗀다" 는 옛 흐름의 잔재다.
     * ComposeModal 이 atomic 발송으로 바뀌면서 첨부는 File 객체로 메모리에만 있다가 발송 시
     * multipart 로 한 번에 올라가므로(mailApi.dispatch), 발송 전 서버에는 첨부가 존재하지 않는다.
     *
     * 서버 엔드포인트(POST /api/mail/attachments/upload, DELETE /api/mail/attachments/{id})는
     * 그대로 살아있다 — 이미 발송·수신된 메일의 첨부를 다루는 경로라 제거 대상이 아니다.
     */

    /**
     * 첨부 라이브러리 — 내 모든 메일의 첨부 검색·페이징.
     * params: { q, folder, dateFrom, dateTo, page, size }
     *   - folder 빈값/생략 = TRASH+SPAM 제외 전체
     *   - dateFrom/dateTo: ISO 8601 datetime (예: "2026-05-01T00:00:00")
     */
    listMyAttachments: (params = {}) =>
        api.get(`${BASE}/attachments/me`, params),

    // ── 다중 서명 (기본 명함 / HTML) ───────────────────
    // 레거시 단수 서명(`/signature/me`) API 는 통합 정리되어 모두 제거됨. 기본 서명은 listSignatures()
    // 결과 중 isDefault=true 인 행을 사용.
    //
    // bodyType 이 본문 필드를 결정한다.
    //   "BASIC" — card(이름·부서·직급·연락처)를 보낸다. bodyHtml 은 서버가 회사 명함 템플릿으로
    //             만들어 채우므로 보내도 무시된다. 편집기에 마크업이 노출되지 않는 방식.
    //   "HTML"  — bodyHtml(마크업)을 보낸다. 서버가 새니타이즈 후 저장하므로 응답은 정제된 소스다.
    //   "TEXT"  — 레거시. 편집기에서 더 이상 고를 수 없다.
    // 삽입·미리보기는 방식과 무관하게 항상 bodyHtml 만 읽으면 된다.
    /** 내 서명 전체 목록 — 응답: [{ id, name, bodyType, card, bodyText, bodyHtml, isDefault, ... }] */
    listSignatures: () => api.get(`${BASE}/signatures/me`),

    /** 새 서명 생성 — body: { name, bodyType, card | bodyHtml, isDefault? } */
    createSignature: (data) => api.post(`${BASE}/signatures/me`, data),

    /** 서명 수정 — body: { name, bodyType, card | bodyHtml } */
    updateSignature: (id, data) => api.put(`${BASE}/signatures/${id}`, data),

    /** 서명 삭제 (soft) */
    deleteSignature: (id) => api.delete(`${BASE}/signatures/${id}`),

    /** 기본 서명 설정 (다른 서명의 isDefault 는 자동 false) */
    setDefaultSignature: (id) => api.post(`${BASE}/signatures/${id}/default`),

    /**
     * 기본(명함) 편집 초깃값 — 응답: { card, hrDept, hrPosition, hrDuty, fromExistingSignature }
     *
     * card 는 "기존 서명 → 인사정보 → 회사 기본값" 순으로 서버가 조립한 한 벌이라
     * 그대로 폼에 넣으면 된다. hr* 는 인사정보 원본으로, 값이 다를 때 칩으로 제시하는 용도다
     * (부서 표기가 인사 DB 와 다른 사람이 많고, 직위 대신 직책을 쓰는 사람도 많다).
     *
     * @param {number} [signatureId] 편집 중인 서명. 생략하면 내 기본 서명에서 값을 빌려온다.
     */
    signatureCardDefaults: (signatureId) =>
        api.get(`${BASE}/signatures/card-defaults`, signatureId ? { signatureId } : {}),

    /**
     * 기본(명함) 미리보기 — 응답: { html }. 저장하지 않는다.
     *
     * 템플릿을 프런트에 복제하지 않고 매번 서버에 렌더를 요청하는 이유: 미리보기와 실제로
     * 발송되는 서명이 갈라지지 않게 하기 위해서다. 서명은 외부로 나가는 물건이라 그 어긋남을
     * 발견하는 시점이 대개 사고 뒤다. (입력 중에는 디바운스해서 호출할 것)
     */
    signatureCardPreview: (card) => api.post(`${BASE}/signatures/card-preview`, card),

    /**
     * 서명에 사용할 이미지 업로드 — 응답: { key, url }
     * url 은 백엔드 프록시 경유 절대 경로 (/api/mail/signatures/images/{key}).
     */
    uploadSignatureImage: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.upload(`${BASE}/signatures/images`, fd);
    },

    // ── 개인 연락처 ──────────────────────────────────
    /** 연락처 목록 (스코프·검색·페이지) — scope: PERSONAL/TEAM/PUBLIC. Page<ContactDto> 반환 */
    listContacts: (scope = "PERSONAL", q = "", page = 0, size = 20) =>
        api.get(`${BASE}/contacts/me`, { scope, q, page, size }),

    /**
     * 사내 전체 주소록 — 재직 직원 명부. Page<CompanyMemberDto> 반환.
     *
     * 개인/팀/공용과 <b>다른 엔드포인트</b>인 이유: 이 탭이 보는 건 연락처가 아니라 직원이다.
     * 응답 항목도 다르다 — { id(직원 id), employeeCode, name, email, phone, department, position, duty }.
     * 조회 전용이라 등록/수정/삭제 짝이 없다.
     */
    listCompanyContacts: (q = "", page = 0, size = 20) =>
        api.get(`${BASE}/contacts/company`, { q, page, size }),

    /** 새 연락처 생성 — body: { name, email, phone?, company?, position?, memo? } */
    createContact: (data) => api.post(`${BASE}/contacts/me`, data),

    /** 연락처 수정 */
    updateContact: (id, data) => api.put(`${BASE}/contacts/${id}`, data),

    /** 연락처 삭제 (soft) */
    deleteContact: (id) => api.delete(`${BASE}/contacts/${id}`),

    /**
     * 연락처 일괄 삭제 — 요청한 id 중 <b>본인이 등록한 것만</b> 삭제된다.
     * 권한 없는 항목은 서버가 조용히 건너뛰고, 응답 수치로 알려준다.
     * @returns { requested, deleted, skippedNotOwned, notFound }
     */
    bulkDeleteContacts: (ids) => api.post(`${BASE}/contacts/bulk-delete`, ids),

    /**
     * 이메일 묶음 중 본인 주소록에 이미 등록된 것만 set 으로 받기 (bulk).
     * 발송 직후 "새 연락처 등록 prompt" 에서 이미 등록된 외부 주소 자동 필터링용.
     */
    contactsExisting: (emails) => api.post(`${BASE}/contacts/me/exists`, emails),

    /**
     * AI 메일 제목 추천 — 본문 텍스트 기반 1~3개 후보 반환.
     * 응답: { model, suggestions: ["제목1", "제목2", "제목3"] }
     */
    suggestSubject: (bodyText) =>
        api.post(`${BASE}/ai/suggest-subject`, { bodyText: bodyText || "" }),

    /**
     * AI 첨부 언급 감지 — 본문에서 작성자가 첨부 파일을 동봉했다고 적었는지 판정.
     * 응답: { mentionsAttachment: true|false, evidence: ["근거 인용", ...], method: "keyword-skip"|"ai"|... }
     * 발송 직전 미리보기 단계에서 호출 — 첨부 안 붙이고 "첨부드립니다" 라고만 적고 보내는 사고 방지.
     */
    detectAttachmentMention: (subject, bodyText) =>
        api.post(`${BASE}/ai/detect-attachment`, {
            subject:  subject  || "",
            bodyText: bodyText || "",
        }),

    /**
     * 관리자가 정한 사내 공통 AI 프롬프트 조회 — 모든 직원이 기본값으로 사용.
     * 응답: { prompt }
     */
    getAiPromptSetting: () => api.get(`${BASE}/ai/prompt-setting`),

    /**
     * 관리자 전용 — 사내 공통 AI 프롬프트 저장.
     * 권한: MAIL_RULE_GLOBAL
     */
    saveAiPromptSetting: (prompt) =>
        api.put(`${BASE}/ai/prompt-setting`, { prompt }),

    // ── 차단 규칙 ───────────────────────────────────
    /** 내 개인 규칙 목록 */
    listMyRules: () => api.get(`${BASE}/rules/me`),

    /**
     * 내 규칙 생성 (USER 스코프). applyRetroactively 플래그로 소급 적용.
     * 응답 MailBlockRuleDto — 소급 적용을 했으면 retroMovedCount 에 이동 건수가 담긴다(안 했으면 null).
     * enabled=false + applyRetroactively=true 조합은 400.
     */
    createRule: (data) => api.post(`${BASE}/rules/me`, data),

    /** 내 규칙 수정 — <b>USER 스코프 전용</b>. GLOBAL 규칙 id 를 주면 403 (updateGlobalRule 을 쓸 것) */
    updateRule: (id, data) => api.put(`${BASE}/rules/${id}`, data),

    /** 내 규칙 삭제 (Soft Delete) — <b>USER 스코프 전용</b>. GLOBAL 규칙 id 를 주면 403 */
    deleteRule: (id) => api.delete(`${BASE}/rules/${id}`),

    /** GLOBAL 규칙 목록 (관리자 전용) — 비활성(enabled=false) 규칙도 포함해 반환 */
    listGlobalRules: () => api.get(`${BASE}/rules/global`),

    /** GLOBAL 규칙 생성 (관리자 전용) — 응답에 retroMovedCount 포함 */
    createGlobalRule: (data) => api.post(`${BASE}/rules/global`, data),

    /** GLOBAL 규칙 수정 (관리자 전용, MAIL_RULE_GLOBAL) — USER 규칙 id 를 주면 400 */
    updateGlobalRule: (ruleId, body) => api.put(`${BASE}/rules/global/${ruleId}`, body),

    /** GLOBAL 규칙 삭제 (관리자 전용, MAIL_RULE_GLOBAL) — USER 규칙 id 를 주면 400 */
    deleteGlobalRule: (ruleId) => api.delete(`${BASE}/rules/global/${ruleId}`),

    /**
     * 내게 실제로 적용되는 규칙 전체 — 권한 불요.
     * 응답: { userRules: [...], globalRules: [...] }
     * globalRules 는 관리자가 만든 사내 공통 규칙이라 일반 사용자에겐 <b>읽기 전용 표시용</b>이다
     * (수정·삭제 버튼을 걸지 말 것).
     */
    listEffectiveRules: () => api.get(`${BASE}/rules/effective`),

    // ── 관리자 ────────────────────────────────────
    /** 미배정 메일 ID 목록 */
    listUnassigned: () => api.get(`${BASE}/admin/unassigned`),

    /** 미배정 메일 수동 배정 */
    reassignMail: (mailId, employeeId) =>
        api.post(`${BASE}/admin/reassign`, null, { params: { mailId, employeeId } }),

    /** 타인 메일 조회 (관리자 — 감사 로그 자동 기록) */
    getMailAsAdmin: (mailId) => api.get(`${BASE}/admin/mail/${mailId}`),

    /** 전체 메일 목록 (관리자) — 결재 관리자 모드처럼 회사 전체 메일. direction=INBOUND|OUTBOUND|ALL */
    adminListAll: (direction = "ALL", q = "", page = 0, size = 50) =>
        api.get(`${BASE}/admin/all`, {
            ...(direction && direction !== "ALL" ? { direction } : {}),
            ...(q && q.trim() ? { q: q.trim() } : {}),
            page, size,
        }),

    /** 미배정 메일 목록 (관리자, 상세) — 아무 받은편지함에도 배정 안 된 수신 메일 */
    adminListUnmatched: (page = 0, size = 50) =>
        api.get(`${BASE}/admin/unmatched`, { page, size }),

    /**
     * 특정 직원의 메일함 (관리자) — 그 사람 시야의 폴더/정렬 그대로.
     * folder 는 INBOX|SENT|DRAFT|SCHEDULED|SPAM|TRASH|ARCHIVE|CUSTOM, "ALL" 이면 전체 보기
     * (INBOX+CUSTOM+ARCHIVE). 응답은 일반 목록과 같은 MailListItemDto 라 폴더·읽음·별표가 그대로 담긴다.
     * 조회 사실은 서버가 READ_OTHER 감사 로그로 페이지당 1건 남긴다.
     */
    adminMailbox: (employeeId, folder = "ALL", q = "", page = 0, size = 30) =>
        api.get(`${BASE}/admin/mailbox`, {
            employeeId,
            ...(folder && folder !== "ALL" ? { folder } : {}),
            ...(q && q.trim() ? { q: q.trim() } : {}),
            page, size,
        }),

    /**
     * 특정 직원의 폴더별 카운트 + 인적사항 (관리자).
     * 응답: { employeeId, employeeName, email, department, position, total: {INBOX:n,…}, unread: {…} }
     * 건수 0인 폴더는 키 자체가 없다 — 화면에서 `?? 0` 으로 읽을 것.
     */
    adminMailboxCounts: (employeeId) =>
        api.get(`${BASE}/admin/mailbox/counts`, { employeeId }),

    // ── 감사 로그 ────────────────────────────────────
    /** 행위자 기준 감사 로그 */
    auditByActor: (employeeId, page = 0, size = 30) =>
        api.get(`${BASE}/audit/actor/${employeeId}`, { page, size }),

    /** 메일 기준 감사 로그 */
    auditByMail: (mailId, page = 0, size = 50) =>
        api.get(`${BASE}/audit/mail/${mailId}`, { page, size }),

    /** 액션 기준 감사 로그 (READ_OTHER, RULE_DELETE 등) */
    auditByAction: (action, page = 0, size = 30) =>
        api.get(`${BASE}/audit/action/${action}`, { page, size }),
};

/** 폴더 메타 — 사이드바·라벨 표시 + 탭 선택 시 본문 위에 보여줄 한 줄 설명 */
export const FOLDERS = [
    { type: "group", label: "메일함" },
    {
        key: "ALL",     label: "전체",          icon: "all", virtual: true,
        desc: "커스텀 폴더로 정리한 메일까지 포함해, 받은 메일을 한 곳에서 모두 보여줍니다 (휴지통·스팸 제외).",
    },
    {
        key: "INBOX",   label: "받은 메일함",   icon: "inbox",
        desc: "외부 또는 내부에서 받은 메일이 도착하는 기본 폴더입니다. 새 메일은 우선 여기로 들어옵니다.",
    },
    {
        // "재시도가 가능합니다" 였는데 재시도 진입로가 없었다 — 없는 기능을 안내하면 사용자가 찾아 헤맨다.
        // 발송 실패분은 status=FAILED 로 임시보관함에 남고, 거기서 '발송' 버튼으로 다시 보낸다.
        key: "SENT",    label: "보낸 메일함",   icon: "send",
        desc: "내가 발송한 모든 메일이 보관됩니다.",
    },
    {
        key: "SELF",    label: "내게 쓴 메일함", icon: "self",
        desc: "내가 나 자신에게 보낸 메일만 모아 보여줍니다. 메모·아이디어 정리용으로 활용하세요.",
        virtual: true,
    },
    {
        // 여기 쌓이는 건 네 종류다:
        //   ① 작성 화면에서 '임시저장' 을 눌러 저장한 것 (첨부는 저장되지 않는다)
        //   ② 예약 발송이 취소되어 되돌아온 것
        //   ③ 발송이 실패한 것 (status=FAILED)
        //   ④ NAS 이관 때 넘어온 이전 시스템의 임시저장 메일
        // '편집' 은 ①에만 열린다 — ②~④ 는 첨부가 붙어 있거나 이관 본문이라 편집이 위험하다
        // (서버가 MailDetailDto.editable 로 판정한다). 그쪽은 '발송' 만 제공하므로 문구도 둘 다 적는다.
        key: "DRAFT",   label: "임시 보관함",   icon: "drafts",
        desc: "임시저장한 메일과, 예약이 취소·실패해 되돌아온 메일이 모입니다. 임시저장한 메일은 다시 열어 편집할 수 있고, 그 외에는 저장된 내용 그대로 발송할 수 있습니다.",
    },
    {
        key: "SCHEDULED", label: "예약함",      icon: "schedule",
        desc: "예약 발송으로 등록되어 지정한 시각에 발송될 메일입니다. 발송 전에는 예약을 취소할 수 있습니다.",
    },
    {
        key: "SPAM",    label: "스팸함",        icon: "report",
        // 자동 영구삭제 스케줄러는 없다(보존 기간 정책 미도입). 예전 "30일 후 자동 삭제" 안내는 사실이 아니었다.
        desc: "스팸으로 분류되었거나 사용자가 직접 등록한 메일입니다. 자동으로 삭제되지 않으며, '스팸함 비우기'로 휴지통에 옮길 수 있습니다.",
    },
    {
        key: "TRASH",   label: "휴지통",        icon: "delete",
        desc: "삭제한 메일이 잠시 보관되는 곳입니다. 복원하거나 영구 삭제할 수 있습니다.",
    },
    // 보관함(ARCHIVE) 고정 항목은 제거 — '내 폴더'의 기본 '보관함' 폴더로 대체.
];
